import { DatePipe } from '@angular/common';
import { Clipboard } from '@angular/cdk/clipboard';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormField } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatError } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltip } from '@angular/material/tooltip';
import { EMPTY, catchError, finalize } from 'rxjs';
import { UserRole } from '../../../../models/user-role.model';
import {
  SupportSessionCode,
  SupportSessionService,
} from '../../../../services/support-session.service';
import { UnitResponseModel } from '../../../units/units.model';

export interface CreateSupportCodeDialogData {
  units: UnitResponseModel[];
  roles: UserRole[];
  currentUnitId?: string;
}

export interface DurationOption {
  minutes: number;
  label: string;
}

function formatDurationLabel(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) {
    return `${mins} minuten`;
  }
  const hoursStr = `${hours} uur`;
  if (mins === 0) {
    return hoursStr;
  }
  return `${hoursStr} ${mins} minuten`;
}

@Component({
  selector: 'app-create-support-code-dialog',
  imports: [
    DatePipe,
    MatButton,
    MatDialogActions,
    MatDialogContent,
    MatDialogTitle,
    MatError,
    MatFormField,
    MatIcon,
    MatIconButton,
    MatSelectModule,
    MatTooltip,
    ReactiveFormsModule,
  ],
  templateUrl: './create-support-code-dialog.component.html',
  styleUrl: './create-support-code-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateSupportCodeDialogComponent {
  protected readonly dialogRef = inject(MatDialogRef<CreateSupportCodeDialogComponent>);
  protected readonly data = inject<CreateSupportCodeDialogData>(MAT_DIALOG_DATA);
  private readonly _clipboard = inject(Clipboard);
  private readonly _fb = inject(NonNullableFormBuilder);
  private readonly _snackbar = inject(MatSnackBar);
  private readonly _supportSessionService = inject(SupportSessionService);

  protected readonly step = signal<'form' | 'result'>('form');
  protected readonly submitting = signal(false);

  protected readonly generatedCode = signal<string | null>(null);
  protected readonly generatedCodeRecord = signal<SupportSessionCode | null>(null);

  protected readonly customDurationOptions: DurationOption[] = Array.from(
    { length: (480 - 15) / 15 + 1 },
    (_, i) => {
      const minutes = 15 + i * 15;
      return {
        minutes,
        label: formatDurationLabel(minutes),
      };
    }
  );

  protected readonly addForm = this._fb.group({
    unitId: [this.data.currentUnitId ?? '', Validators.required],
    userRoleIds: [[] as string[], Validators.required],
    durationType: ['1h' as '30m' | '1h' | '2h' | 'custom', Validators.required],
    customDurationMinutes: [60, [Validators.required, Validators.min(15), Validators.max(480)]],
  });

  setDurationType(type: '30m' | '1h' | '2h' | 'custom'): void {
    this.addForm.controls.durationType.setValue(type);
  }

  handleAdd(): void {
    if (this.addForm.invalid || this.submitting()) return;

    const { unitId, userRoleIds, durationType, customDurationMinutes } =
      this.addForm.getRawValue();

    let durationMinutes = 60;
    if (durationType === '30m') {
      durationMinutes = 30;
    } else if (durationType === '1h') {
      durationMinutes = 60;
    } else if (durationType === '2h') {
      durationMinutes = 120;
    } else if (durationType === 'custom') {
      durationMinutes = customDurationMinutes;
    }

    this.submitting.set(true);
    this._supportSessionService
      .createCode({
        unitId,
        userRoleIds,
        durationMinutes,
      })
      .pipe(
        catchError(() => {
          this._snackbar.open('Aanmaken van de code is mislukt', 'OK');
          return EMPTY;
        }),
        finalize(() => this.submitting.set(false))
      )
      .subscribe((created) => {
        this.generatedCode.set(created.code);
        this.generatedCodeRecord.set(created.codeRecord);
        this.step.set('result');
      });
  }

  copyGeneratedCode(): void {
    const code = this.generatedCode();
    if (!code) return;

    const success = this._clipboard.copy(code);
    if (success) {
      this._snackbar.open('Code gekopieerd naar klembord', undefined, {
        duration: 2000,
      });
    } else {
      this._snackbar.open('Kopiëren is mislukt', 'OK');
    }
  }

  getDurationText(createdAt?: string, expiresAt?: string): string {
    if (!createdAt || !expiresAt) return '—';
    const start = new Date(createdAt).getTime();
    const end = new Date(expiresAt).getTime();
    const diffMinutes = Math.round((end - start) / 60000);
    if (diffMinutes <= 0) return '—';
    return formatDurationLabel(diffMinutes);
  }

  closeDialog(): void {
    this.dialogRef.close(this.step() === 'result');
  }
}

